import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../api/client';
import { Loader2, Plus, Trash2, KeyRound, ShieldCheck, X } from 'lucide-react';

// Kept in canonical order — must match backend routes/rbac.py PERMISSION_KEYS
const PERMS = [
  { key: 'products',         label: 'Products',         hint: 'Create / edit / delete products and categories' },
  { key: 'orders',           label: 'Orders',           hint: 'View & update order statuses (Dispatched, Delivered)' },
  { key: 'seo',              label: 'SEO & Marketing',  hint: 'City pages, sitemaps, category intros' },
  { key: 'emails',           label: 'Emails',           hint: 'Edit templates for orders, verification, abandoned carts' },
  { key: 'reviews',          label: 'Reviews',          hint: 'Approve or reject customer reviews' },
  { key: 'sales_reports',    label: 'Sales Reports',    hint: 'Revenue, profit, daily sales data' },
  { key: 'discounts',        label: 'Discounts',        hint: 'Create & manage coupon codes' },
  { key: 'customer_privacy', label: 'Customer Privacy', hint: 'View full customer contact details & addresses' },
];

const emptyPerms = () => PERMS.reduce((acc, p) => { acc[p.key] = false; return acc; }, {});

const ManageAdminsAdmin = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);   // admin object or null
  const [form, setForm] = useState({ name: '', email: '', password: '', permissions: emptyPerms() });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/manage-admins');
      setAdmins(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load admins');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', email: '', password: '', permissions: emptyPerms() });
    setCreating(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ name: a.name, email: a.email, password: '', permissions: { ...a.permissions } });
    setCreating(true);
  };
  const close = () => { setCreating(false); setEditing(null); };

  const togglePerm = (key) => setForm(f => ({ ...f, permissions: { ...f.permissions, [key]: !f.permissions[key] } }));

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, permissions: form.permissions };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/manage-admins/${editing.id}`, payload);
        toast.success('Admin updated');
      } else {
        if (!form.email || !form.name || !form.password) {
          toast.error('Name, email and password are required'); return;
        }
        await api.post('/admin/manage-admins', form);
        toast.success('Admin created — share credentials manually');
      }
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.email} as admin? Their user account will remain but they will lose admin access.`)) return;
    try {
      await api.delete(`/admin/manage-admins/${a.id}`);
      toast.success('Admin removed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#F46A25]" /></div>;

  return (
    <div className="space-y-4" data-testid="manage-admins-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-black text-[#12263A] flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#F46A25]" /> Manage Admins</h2>
          <p className="text-xs text-neutral-600 mt-1 max-w-xl">
            Invite colleagues and grant granular access. The Super Admin is immutable — only they can add or remove admins and change permissions.
          </p>
        </div>
        <button data-testid="add-admin-btn" onClick={openCreate} className="bg-[#F46A25] hover:bg-[#e05a15] text-white text-sm font-bold px-4 py-2 rounded-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Admin
        </button>
      </div>

      <div className="border border-neutral-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[11px] uppercase text-neutral-600 font-bold">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Email</th>
              <th className="text-left px-3 py-2">Role</th>
              <th className="text-left px-3 py-2">Permissions</th>
              <th className="text-left px-3 py-2">Last Login</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map(a => {
              const activePerms = Object.entries(a.permissions).filter(([, v]) => v).map(([k]) => k);
              return (
                <tr key={a.id} className="border-t border-neutral-100" data-testid={`admin-row-${a.id}`}>
                  <td className="px-3 py-2 font-semibold text-[#12263A]">{a.name}</td>
                  <td className="px-3 py-2 text-neutral-700">{a.email}</td>
                  <td className="px-3 py-2">
                    {a.is_super_admin
                      ? <span className="inline-block bg-[#F46A25] text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm">Super Admin</span>
                      : <span className="inline-block bg-neutral-100 text-neutral-700 text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm">Admin</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-neutral-600">
                    {a.is_super_admin ? 'All (implicit)' : (activePerms.length ? activePerms.join(', ') : <span className="text-neutral-400 italic">none</span>)}
                  </td>
                  <td className="px-3 py-2 text-xs text-neutral-500">{a.last_login_at ? new Date(a.last_login_at).toLocaleString() : '—'}</td>
                  <td className="px-3 py-2 text-right">
                    {a.is_super_admin ? (
                      <span className="text-[11px] text-neutral-400 italic">Immutable</span>
                    ) : (
                      <div className="inline-flex gap-1">
                        <button onClick={() => openEdit(a)} data-testid={`edit-admin-${a.id}`} className="text-xs text-[#12263A] hover:text-[#F46A25] px-2 py-1 border border-neutral-200 rounded-sm flex items-center gap-1">
                          <KeyRound className="w-3 h-3" /> Edit
                        </button>
                        <button onClick={() => remove(a)} data-testid={`remove-admin-${a.id}`} className="text-xs text-red-600 hover:text-red-800 px-2 py-1 border border-neutral-200 rounded-sm flex items-center gap-1">
                          <Trash2 className="w-3 h-3" /> Remove
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={close} data-testid="admin-form-modal">
          <div className="bg-white rounded-sm border border-neutral-200 max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
              <h3 className="font-black text-[#12263A]">{editing ? `Edit ${editing.email}` : 'Add new admin'}</h3>
              <button onClick={close} className="text-neutral-400 hover:text-[#12263A]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[11px] uppercase text-neutral-600 font-bold">Name</span>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm mt-1 focus:outline-none focus:border-[#F46A25]" data-testid="admin-form-name" />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase text-neutral-600 font-bold">Email</span>
                  <input type="email" value={form.email} disabled={!!editing} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm mt-1 focus:outline-none focus:border-[#F46A25] disabled:bg-neutral-50" data-testid="admin-form-email" />
                </label>
              </div>
              <label className="block">
                <span className="text-[11px] uppercase text-neutral-600 font-bold">{editing ? 'New password (leave blank to keep current)' : 'Initial password'}</span>
                <input type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder={editing ? '••••••••' : 'Min 8 chars — share with the colleague manually'} className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm mt-1 font-mono focus:outline-none focus:border-[#F46A25]" data-testid="admin-form-password" />
              </label>
              <div>
                <div className="text-[11px] uppercase text-neutral-600 font-bold mb-2">Permissions</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {PERMS.map(p => (
                    <label key={p.key} className="flex items-start gap-3 p-3 border border-neutral-200 rounded-sm hover:border-[#F46A25] cursor-pointer" data-testid={`perm-toggle-${p.key}`}>
                      <input type="checkbox" checked={!!form.permissions[p.key]} onChange={() => togglePerm(p.key)} className="mt-0.5 w-4 h-4 accent-[#F46A25]" />
                      <div>
                        <div className="text-sm font-semibold text-[#12263A]">{p.label}</div>
                        <div className="text-[11px] text-neutral-500 leading-snug">{p.hint}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="mt-2 text-[11px] text-neutral-500 italic">
                  Note: <b>Store Settings</b> (Stripe keys, tax, business info) is Super-Admin only and cannot be delegated.
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-neutral-200 bg-neutral-50">
              <button onClick={close} className="text-sm text-neutral-600 hover:text-[#12263A] px-3 py-1.5">Cancel</button>
              <button onClick={save} data-testid="admin-form-save" className="bg-[#F46A25] hover:bg-[#e05a15] text-white text-sm font-bold px-4 py-2 rounded-sm">
                {editing ? 'Save changes' : 'Create admin'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageAdminsAdmin;
